import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Building2, MessageSquare, Megaphone, HeartHandshake } from 'lucide-react';

/**
 * Contact page — dedicated public URL for AdSense compliance + customer trust.
 */
export default function Contact() {
  return (
    <div className="min-h-screen bg-rooam-black text-white">
      <Helmet>
        <title>Contact Rooam Sports Network — Support, Partnerships, and Press</title>
        <meta name="description" content="Get in touch with Rooam Sports Network. Customer support, tournament-director partnerships, press inquiries, advertising, and general questions — all routed to a real person." />
        <link rel="canonical" href="https://rooamsports.network/contact" />
        <meta property="og:url" content="https://rooamsports.network/contact" />
        <meta property="og:title" content="Contact Rooam Sports Network" />
      </Helmet>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <header className="mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-red-500 font-bold mb-3">Contact</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
            HOW CAN WE HELP?
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Every email lands with a real person on the Rooam team. We typically respond within one business day; tournament directors with live games on a deadline always get same-day support.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {[
            { icon: <MessageSquare className="w-5 h-5 text-cyan-400" />, title: 'Customer Support', body: 'Account access, login issues, streaming questions, payments, subscriptions.', email: 'support@rooamsports.network' },
            { icon: <HeartHandshake className="w-5 h-5 text-emerald-400" />, title: 'Tournament Director Partnerships', body: 'Run an AAU circuit, league, showcase, or tournament? Get streaming + the 70/30 revenue share live within a week.', email: 'partners@rooamsports.network' },
            { icon: <Megaphone className="w-5 h-5 text-amber-400" />, title: 'Advertising & Sponsorship', body: 'Brands, agencies, and local sponsors who want to reach youth and amateur sports audiences.', email: 'ads@rooamsports.network' },
            { icon: <Building2 className="w-5 h-5 text-pink-400" />, title: 'Press & Media', body: 'Story pitches, interviews with the founding team, product launch coverage, investor materials.', email: 'press@rooamsports.network' },
          ].map((cat, i) => (
            <div key={i} className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-5">
              <div className="flex items-center gap-2 mb-2">
                {cat.icon}
                <h2 className="text-white font-bold text-base">{cat.title}</h2>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-3">{cat.body}</p>
              <a href={`mailto:${cat.email}`} className="text-red-400 hover:text-red-300 text-sm font-semibold inline-flex items-center gap-1.5" data-testid={`contact-${cat.email}`}>
                <Mail className="w-3.5 h-3.5" />
                {cat.email}
              </a>
            </div>
          ))}
        </section>

        <section className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-6">
          <h2 className="text-xl font-black text-white mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>COMPANY DETAILS</h2>
          <div className="space-y-3 text-sm text-zinc-300">
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-white">Rooam Sports Network, LLC</p>
                <p className="text-zinc-500 text-xs mt-0.5">A registered limited-liability company in the State of Florida.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
              <div>
                <p>For mailing or in-person business inquiries, please email <a href="mailto:support@rooamsports.network" className="text-red-400 hover:text-red-300">support@rooamsports.network</a> first — we&apos;ll respond with the appropriate physical address.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
              <div>
                <p>General inbox: <a href="mailto:hello@rooamsports.network" className="text-red-400 hover:text-red-300">hello@rooamsports.network</a></p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
